import { z } from 'zod'

export const DTOOLS_SI_QUOTE_ACTIONS = ['import', 'export', 'export_shell', 'import_change_order', 'replace'] as const
export type DtoolsSiQuoteAction = (typeof DTOOLS_SI_QUOTE_ACTIONS)[number]

const KNOWN_DTOOLS_SI_ACTIONS = new Set<string>(DTOOLS_SI_QUOTE_ACTIONS)

export const dtoolsSiLinkedProjectSchema = z.object({
  project_id: z.string(),
  project_number: z.string().nullable(),
  last_state: z.number().nullable().optional(),
  current_co_number: z.number().nullable().optional(),
  last_published_on: z.string().nullable().optional(),
  last_confirmed_on: z.string().nullable().optional(),
})
export type DtoolsSiLinkedProject = z.infer<typeof dtoolsSiLinkedProjectSchema>

export const dtoolsSiChangeOrderImportSchema = z.object({
  si_project_id: z.string(),
  si_project_number: z.string(),
  imported_co_numbers: z.array(z.number()),
  highest_imported_co_number: z.number().nullable(),
  // Lowest SI-approved CO not yet imported (unapproved COs are skipped);
  // absent/null when the server could not read SI.
  next_co_number: z.number().nullable().optional(),
})
export type DtoolsSiChangeOrderImport = z.infer<typeof dtoolsSiChangeOrderImportSchema>

export const dtoolsSiReplacementContextSchema = z.object({
  eligible: z.boolean(),
  reason: z.string().nullable(),
})
export type DtoolsSiReplacementContext = z.infer<typeof dtoolsSiReplacementContextSchema>

// Why `import_change_order` is withheld: the sales order already has an open
// (Draft/Active) change-order quote, so the import would 409.
export const dtoolsSiChangeOrderImportBlockedSchema = z.object({
  reason: z.string(),
  open_change_order: z.object({
    quote_id: z.string(),
    quote_number: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
  }),
})
export type DtoolsSiChangeOrderImportBlocked = z.infer<typeof dtoolsSiChangeOrderImportBlockedSchema>

const dtoolsSiBlockingStateSchema = z.object({
  kind: z.literal('imported_ack_pending'),
  sync_log_id: z.string(),
  message: z.string(),
})

export const dtoolsSiQuoteActionsSchema = z.object({
  actions: z
    .array(z.string())
    .transform((arr) => arr.filter((action): action is DtoolsSiQuoteAction => KNOWN_DTOOLS_SI_ACTIONS.has(action))),
  branch_number: z.number().nullable(),
  dtools_si_project: dtoolsSiLinkedProjectSchema.nullable().optional().default(null),
  change_order_import: dtoolsSiChangeOrderImportSchema.nullable().optional().default(null),
  change_order_import_blocked: dtoolsSiChangeOrderImportBlockedSchema.nullable().optional().default(null),
  replacement: dtoolsSiReplacementContextSchema.nullable().optional().default(null),
  blocking_state: dtoolsSiBlockingStateSchema.nullable().optional().default(null),
})
export type DtoolsSiQuoteActions = z.infer<typeof dtoolsSiQuoteActionsSchema>
